import { z } from "zod";

/**
 * Shared DTO cho Admin Identity Index lookup + Social account ops
 * (US-AE3.F5-0013 / @UC9024 identity-lookup · @UC9025 social-ops).
 * Một nguồn sự thật api ↔ admin (DRY).
 *
 * BẢO MẬT (invariants):
 * - C2: keyValue (PII) + providerUid/emailAtProvider OPTIONAL — backend redact theo vai.
 * - C3/AC5: `.strict()` — KHÔNG khai báo rawProfile/providerToken/secret; field lạ bị reject.
 * - Read-first: lookup chỉ tra, không free-edit identity_index.
 */

/** Mã cảnh báo: social là phương thức đăng nhập DUY NHẤT → CHẶN thu hồi (AC3). */
export const SOCIAL_LAST_LOGIN_METHOD = "LAST_LOGIN_METHOD";

/** Độ dài tối đa key tra cứu (email/SĐT) — chặn input phình. */
export const IDENTITY_KEY_MAX_LENGTH = 256;

/**
 * Provider social được hỗ trợ (đồng nhất identity_index.source / social_accounts.provider).
 *
 * 🔴 Export để `social-provider-coverage.spec.ts` (@UC9548) khoá lại: thiếu một provider ở đây
 * thì màn admin `GET /admin/users/:id/social` **bỏ qua lặng lẽ** row của provider đó
 * (`toSocialAccountItem` trả null) ⇒ admin nhìn user đăng nhập bằng nó thấy danh sách RỖNG,
 * và `DELETE .../social/:provider` không thu hồi được. Đó là mù, không phải an toàn.
 */
export const socialProviderSchema = z.enum(["zalo", "google", "facebook", "apple"]);

/**
 * Nguồn identity_index (gồm cả email_verified — không phải social provider).
 *
 * EXPORT (US-E24.F17-0366-S02 / @UC9621): đây là **closed-set** mà mặt admin chấp nhận —
 * `toIdentityMatch` loại bỏ IM LẶNG mọi giá trị ngoài enum. Đường ghi nào cũng phải parse được
 * bằng schema NÀY, nếu không hàng vừa ghi sẽ không hiện ở đâu cả. Export để test của đường ghi
 * khoá được điều đó tại biên thay vì tin vào một chuỗi chép tay.
 *
 * 🔴 **SUY TỪ `socialProviderSchema`, không chép tay** (US-E25.F3-0386-S01 / @UC9548): bản chép tay
 * `["zalo","google","facebook","email_verified"]` chính là danh sách thứ tám mà `@UC9548` cấm —
 * provider thứ tư (`apple`) sẽ bị `toIdentityMatch` **loại IM LẶNG**, admin tra user Apple ra rỗng.
 */
export const identitySourceSchema = z.enum([
  ...socialProviderSchema.options,
  "email_verified",
] as const);

/** Query GET /admin/identity?key= — key bắt buộc (email hoặc SĐT E.164). */
export const identityLookupQuerySchema = z.object({
  key: z.string().min(1).max(IDENTITY_KEY_MAX_LENGTH),
});

export type IdentityLookupQuery = z.infer<typeof identityLookupQuerySchema>;

/** 1 match identity_index. keyValue OPTIONAL (redact PII theo vai). */
const identityMatchSchema = z
  .object({
    userId: z.string(),
    keyType: z.enum(["email", "phone"]),
    keyValue: z.string().nullable().optional(),
    source: identitySourceSchema,
  })
  .strict();

/** Response GET /admin/identity — matches (rỗng nếu không khớp). */
export const identityLookupResponseSchema = z.object({
  matches: z.array(identityMatchSchema),
});

export type IdentityLookupResponse = z.infer<typeof identityLookupResponseSchema>;
export type IdentityMatch = z.infer<typeof identityMatchSchema>;

/** 1 social account. providerUid/emailAtProvider OPTIONAL (redact). KHÔNG rawProfile/token. */
const socialAccountSchema = z
  .object({
    provider: socialProviderSchema,
    providerUid: z.string().nullable().optional(),
    emailAtProvider: z.string().nullable().optional(),
    createdAt: z.string(),
  })
  .strict();

/** Response GET /admin/users/:id/social — danh sách social link (no secret). */
export const socialAccountsResponseSchema = z.object({
  data: z.array(socialAccountSchema),
});

export type SocialAccountsResponse = z.infer<typeof socialAccountsResponseSchema>;
export type SocialAccountItem = z.infer<typeof socialAccountSchema>;

/**
 * Response DELETE /admin/users/:id/social/:provider.
 * warning='LAST_LOGIN_METHOD' ⇒ CHẶN thu hồi (revokedSessionCount=0), giữ link.
 */
export const revokeSocialResponseSchema = z.object({
  userId: z.string(),
  provider: socialProviderSchema,
  revokedSessionCount: z.number().int().min(0),
  warning: z.literal(SOCIAL_LAST_LOGIN_METHOD).optional(),
});

export type RevokeSocialResponse = z.infer<typeof revokeSocialResponseSchema>;
